import React, { FC, Suspense, useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import {
  Asset,
  Balance,
  BalanceAccount,
  MsgSecureWithdraw,
  MsgSwap,
  validateAddress,
} from "rujira.js";
import { Button, Decimal, i18n, Numeric, Provider } from "rujira.ui";
import { usePreloadedBalance } from "../../../common/components/Balance";
import {
  Destination,
  DestinationOption,
  DestinationSelect,
} from "../../../common/components/Destination";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";
import { useAccounts } from "../../../services/accounts";
import { Quote, useQuote } from "../../../services/useQuote";
import { WithdrawQuery } from "./__generated__/WithdrawQuery.graphql";

const query = graphql`
  query WithdrawQuery {
    thorchainV2 {
      outboundFees {
        asset {
          metadata {
            symbol
          }
          variants {
            secured {
              asset
            }
          }
        }
        outboundFee
      }
      rune {
        price {
          current
        }
        metadata {
          decimals
        }
      }
      pools {
        status
        asset {
          asset
          type
          chain
          price {
            current
          }
          metadata {
            symbol
            decimals
          }
          variants {
            secured {
              chain
              type
              asset
              variants {
                native {
                  denom
                }
              }
              metadata {
                symbol
                decimals
              }
            }
          }
        }
        assetTorPrice
      }
    }
  }
`;

export const Withdraw: React.FC<{
  cancel: () => void;
}> = ({ cancel }) => {
  const [amount, setAmount] = useState(1000n);
  const [destination, setDestination] = useState<Destination>();
  const { balance, account } = usePreloadedBalance();
  const shouldSwap = destination?.asset.chain !== account?.asset.chain;
  const req = useMemo(() => {
    if (!account?.asset) return null;
    if (!destination) return null;
    if (!amount) return null;
    return {
      from: account.asset.asset,
      to: destination.asset.asset,
      amount,
      destination: destination.address,
    };
  }, [account, amount, destination]);
  const quote = useQuote(req);
  const swapFee = shouldSwap
    ? (typeof quote === "object" && "memo" in quote && quote.fees?.total) || 0n
    : 0n;

  const max = account?.balance || 0n;
  if (!balance) return null;
  return (
    <>
      <div
        className="text-right fs-12 condensed color-grey py-1 px-0.5 pointer"
        onClick={() => {
          setAmount(max);
        }}>
        Balance:{" "}
        <span className="color-white">
          <Decimal amount={max} />
        </span>
      </div>
      <Numeric
        label="Amount"
        amount={amount}
        onChangeAmount={setAmount}
        className="mb-2"
      />
      <Suspense>
        <QueryContent
          amount={amount}
          balance={balance}
          destination={destination}
          setDestination={setDestination}
          swapFee={swapFee}
        />
      </Suspense>
      <div className="modal__footer mt-4 px-3 py-2 text-right">
        <Button
          className="button--grey button--outline"
          onClick={cancel}
          label="Cancel"
        />
        <Confirm
          amount={amount}
          destination={destination}
          account={account}
          shouldSwap={shouldSwap}
          quote={quote}
        />
        {/* <Button className="button ml-1" label="Coming Soon..." disabled /> */}
      </div>
    </>
  );
};

const QueryContent: FC<{
  amount: bigint;
  balance: Balance;
  destination?: Destination;
  setDestination: (v: Destination) => void;
  swapFee: bigint;
}> = ({ amount, balance, destination, setDestination, swapFee }) => {
  const q = useLazyLoadQuery<WithdrawQuery>(query, {});
  const { accounts } = useAccounts();
  const outboundFee = q.thorchainV2?.outboundFees.find(
    (a) => a.asset.variants.secured?.asset === balance.asset.asset
  );
  const options: DestinationOption[] = (q.thorchainV2?.pools || [])
    .reduce((a: Asset[], v) => {
      if (v.status !== "AVAILABLE") return a;
      if (v.asset.chain === "THOR") return a;
      if (!v.asset.metadata.symbol.includes(balance.asset.metadata.symbol))
        return a;
      return [v.asset, ...a];
    }, [])
    .flatMap(collectOptions(accounts || []));

  const fee = BigInt(outboundFee?.outboundFee || 0) + swapFee;

  return (
    <>
      <i18n.h2 className="fs-14 fw-500 color-grey mb-1 ml-1">
        Select Destination Account
      </i18n.h2>

      <div className="overflow-y common-scrollbars flex dir-c gap-1 mt-1">
        <DestinationSelect
          destination={destination}
          setDestination={setDestination}
          options={options}
        />
      </div>
      {/* <p className="fs-14 color-grey mt-2 balance">
        Something something outbound delay
      </p> */}
      {/* <Warning
        color="orange"
        msg={`Outbound delay expected.\nThe withdrawal route is not guaranteed to be fast. Please be patient.`}
        className="fs-12 fw-600 mt-2 color-white warning--pre">
        <Stopwatch className="color-orange" />
      </Warning> */}
      <div className="row wrap mt-1.5 mt-sm-2 condensed fs-14 px-2 gap-y-1">
        <i18n.div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
          Withdrawal Fee
        </i18n.div>
        <div className="col-12 col-xs-8 text-center text-xs-right">
          <Decimal
            amount={fee}
            symbol={outboundFee?.asset.metadata.symbol}
            symbolClassName="color-grey fs-14 condensed fw-500"
          />
        </div>
        <i18n.div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
          Total Received
        </i18n.div>
        <div className="col-12 col-xs-8 text-center text-xs-right">
          <Decimal
            amount={fee > amount ? 0n : amount - fee}
            symbol={outboundFee?.asset.metadata.symbol}
            symbolClassName="color-grey fs-14 condensed fw-500"
          />
        </div>
      </div>
    </>
  );
};

const Confirm: FC<{
  amount: bigint;
  account?: BalanceAccount;
  destination?: Destination;
  shouldSwap: boolean;
  quote: Quote;
}> = ({ account, destination, amount, shouldSwap, quote }) => {
  const { selected } = useAccounts();

  const msg = useMemo(() => {
    if (!amount) return null;
    if (!account) return null;
    if (!selected) return null;
    if (!destination) return null;

    return shouldSwap
      ? typeof quote === "object" && "memo" in quote
        ? new MsgSwap(
            { address: selected.address, network: "THOR" },
            account.asset,
            amount,
            quote.memo
          )
        : null
      : new MsgSecureWithdraw(
          { address: selected.address, network: "THOR" },
          account.asset,
          amount,
          destination.address
        );
  }, [amount, destination, account, quote, selected]);

  return (
    <MsgProvider msg={msg}>
      <TxButton className="button ml-1" label="Confirm" onSuccess={close} />
    </MsgProvider>
  );
};

const collectOptions =
  (accounts: { address: string; provider: Provider.Key }[]) =>
  (asset: Asset): DestinationOption[] => {
    return accounts.reduce(
      (a: DestinationOption[], v) =>
        validateAddress(asset.chain, v.address)
          ? [
              {
                asset,
                address: v.address,
                network: asset.chain,
                providers: [v.provider],
              },
              ...a,
            ]
          : a,
      []
    );
  };
